import * as React from 'react';
import { useEffect, useState } from 'react';
import CookieConsent, { getCookieConsentValue } from 'react-cookie-consent';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import config from 'src/constants';
import { Tracking } from '../Tracking/Tracking';
import { getTrackingCodes } from 'src/services/api/company';
import { useSubscription } from 'src/hooks';

const messages = defineMessages({
    cookieConsent: {
        id: 'cookieConsent',
        defaultMessage: 'Az oldal böngészésével hozzájárulsz a sütik használatához. További info az <a>Adatkezelési tájékoztatóban</a>.'
    },
    cookieConsentAccept: {
        id: 'cookieConsentAccept',
        defaultMessage: 'Elfogadom'
    },
    cookieConsentDecline: {
        id: 'cookieConsentDecline',
        defaultMessage: 'Elutasítom'
    }
});

export interface ICookieConsentProps {
    barionPixelIds: string[];
}

const CookieConsentFooter: React.FunctionComponent<ICookieConsentProps> = ({ barionPixelIds }) => {
    const cookieName = 'cookieconsent_status';
    const intl = useIntl();
    const [hasConsent, setHasConsent] = useState(false);
    const [trackingCodes] = useSubscription(getTrackingCodes);
    const [gaClientId, setGaClientId] = useState<string | null>(null);
    const acceptText = intl.formatMessage(messages.cookieConsentAccept);
    const declineText = intl.formatMessage(messages.cookieConsentDecline);

    useEffect(() => {
        const cookieConsentValue = getCookieConsentValue(cookieName);

        if (cookieConsentValue === 'accepted') {
            handleAcceptCookie();
        }
    }, []);

    useEffect(() => {
        const handlePostMessage = (event: MessageEvent) => {
            if (event.origin !== trackingCodes?.GASourceDomain) {
                return;
            }

            if (event.data.type === 'cookie-consent' && event.data.cookieConsent) {
                document.getElementById('rcc-confirm-button')?.click();
            }

            if (event.data.type === 'ga-client-id' && event.data.gaClientId) {
                setGaClientId(event.data.gaClientId);
            }
        };

        window.addEventListener('message', handlePostMessage);

        return () => {
            window.removeEventListener('message', handlePostMessage);
        };
    }, [trackingCodes]);

    const handleAcceptCookie = () => {
        setHasConsent(true);
    };

    const handleDeclineCookie = () => {
        setHasConsent(false);
    };

    return (
        <>
            <CookieConsent
                cookieName={cookieName}
                cookieValue="accepted"
                declineCookieValue="declined"
                disableStyles={true}
                enableDeclineButton={true}
                containerClasses='cookie-consent'
                contentClasses='cookie-consent__content'
                buttonClasses='button button--primary cookie-consent__button'
                declineButtonClasses='button button--secondary cookie-consent__button cookie-consent__button--decline'
                buttonWrapperClasses='cookie-consent__button-wrapper'
                buttonText={acceptText}
                declineButtonText={declineText}
                onAccept={handleAcceptCookie}
                onDecline={handleDeclineCookie}>
                <FormattedMessage {...messages.cookieConsent} values={{
                    a: (msg: string) =>
                        <a className='link' target='_blank' rel='noopener noreferrer'
                            href={getCookieConsentLink(intl.locale)}>
                            {msg}
                        </a>
                }} />
            </CookieConsent>
            <Tracking
                barionPixelIds={barionPixelIds}
                hasConsent={hasConsent}
                gaClientId={gaClientId}
                trackingCodes={trackingCodes} />
        </>

    )
}

const getCookieConsentLink = (language: string): string => {
    return `${config.COOKIE_CONSENT_LINK}${language}.html`;
}

export default CookieConsentFooter;
